import type { Dispatch, Store } from 'vuex';
import type { StoreType } from '../store/state';
import {
  getFolder,
  getFolderContent,
  createFolder,
  renameFolder,
  removeItemFromFolder,
  getFolderAncestry,
  updateFolderSortKeyAndParent,
  deleteFolder
} from '@mimir/web/api/globalClientFolder';
import { logErrorAndShowToast } from '../util/uiError';
import type { AncestryResponseDto, FolderDto, FolderPathSegmentDto, FolderViewModel } from '@mimir/common/dto/folderModels';
import { FolderKind, FolderState, FolderTypeName, validateFolderName } from '@mimir/common/dto/folderModels';
import {
  folders, cacheFolderPath, renameFolderInTree, updateWorkFolder, invalidatePathCacheForFolder,
  fetchSubfolders, setWorkFolder, setCurrentFolder, removeFolderFromTree, doExpandFolder, addFolderToParent,
  currentFolderPath, getUncachedPathStringifiedForFolder,
  updateSortKeyInTree, updateFolderOwnerInTree,
  moveFolder, initFolderTree
} from './folders';
import type { Folder } from '../pages/folders/util';
import { doMoveOrCopyOperation, getOpTypeOnCtrlOrAltHold, getPathStringifiedForFolder, isAnyFolderInSelectedItems, isFolderViewModel } from '../pages/folders/util';
import type { SearchResultHit, FolderSearchResultHit, LoadedSearchItem, ClientItem } from '@mimir/common/dto/searchResultHit';
import { ItemType } from '@mimir/common/dto/itemTypes';
import { saveWorkFolderId } from './userSettings';
import Vue from 'vue';
import EventBus, { showToast, TOAST, EVENTS } from '@mimir/web/eventBus';
import { features } from '../store';
import { invariant } from '@mimir/common/util/invariant';
import type { ItemsDroppedEvent } from '../pages/search/components/search-page-interfaces';
import type { DroppableItem } from '@mimir/client-common/util/droppedItems';
import { getNonFoldersCount, getBaseHtmlForItemsDropped, getMovableAndNonMovableItems } from '@mimir/client-common/util/droppedItems';
import { prepareLocalPathInfo } from '../desktop/utils';
import { defer } from '@mimir/common/util/general';
import flatten from 'lodash/flatten';
import { getFolderFromCacheOrServer, goToFolder } from '../util/folder';
import { tryToGetFoldersToRootFromCache } from '../util/path-to-root';
import type { BaseDialogProps } from '../dialogs/types';
import type { EditDialogProps } from '../dialogs/editDialogRequest';
import { showEditDialog } from '../dialogs/edit';
import { hidePleaseWaitDialog, showPleaseWaitDialog } from '../dialogs/pleaseWait';
import { showMoveOrCopyDialog } from '../dialogs/moveOrCopy';
import { showImportFolderDialog as showImportFolderDialog_ } from '../dialogs/importFolder';
import { SafeHtmlBuilder } from '@mimir/common/util/safeHtmlBuilder';
import { getI18n } from '@mimir/common/i18n/initializeI18n';
import type { AdobePanelContext, ElectronContext } from '@mimir/common/context/applicationContext';
import { canMoveItems } from '@mimir/common/util/security';
import type { User } from '../model/User';
import { ItemState } from '@mimir/common/dto/itemState';
import { FOLDER_DEFAULT_MDF_ID } from '@mimir/common/mdf/mdfDefaults';
import { OperationType } from '@mimir/common/dto/archiveOperationDto';
import type { ArchiveFolder } from '@mimir/web/dialogs/archiveBulkDialogRequest';
import { UpdateType } from '@mimir/web/dialogs/archiveBulkDialogRequest';
import { showArchiveBulkDialog } from '@mimir/web/dialogs/archiveBulk';
import { showSimpleAsyncDialog } from '@mimir/web/dialogs/simpleAsync';
import { calculateSortKey } from '@mimir/common/util/folderLexicalOrdering';
import { isFolderUpdatableByUser } from '@mimir/common/util/itemAccessControl';
import { globalBackendClient } from '@mimir/client-common/globals/backendClient.js';
import { showFolderDeleteDialog } from '@mimir/web/dialogs/folderDelete';
import { showFolderDeleteConfirmDialog } from '@mimir/web/dialogs/folderDeleteConfirm';
import type { FolderDeleteConfirmDialogRequest } from '@mimir/web/dialogs/folderDeleteConfirmDialogRequest';
import type { SearchState } from '../model/searchState';

export async function getFoldersAncestryFromServer(searchState: SearchState, listOfFolderIds: string[]) {
  // TODO: Remove setPathsForNewFolderHitsHasBeenLoaded dependency
  if (!listOfFolderIds.length) {
    searchState.ops.setPathsForNewFolderHitsHasBeenLoaded(true);
    return;
  }
  try {
    const paths = await getFolderAncestry(listOfFolderIds, 'folder');
    for (const folderId of listOfFolderIds) {
      const resolvedPath = paths[folderId];
      if (resolvedPath) {
        const flattenItemAncestors = Object.entries(resolvedPath.paths).reduce(
          (acc: FolderPathSegmentDto[][], [_, ancestor]) => acc.concat(ancestor),
          [],
        );

        cacheFolderPath(folderId, flattenItemAncestors[0]);
      }
    }
    searchState.ops.setListOfFolderIdsInLastSearchResult([]);
    searchState.ops.setPathsForNewFolderHitsHasBeenLoaded(true);
  } catch (err) {
    logErrorAndShowToast('Failed to load folder, please try again.', err, 'ex in getFoldersAncestryFromServer ');
  }
}

export async function loadFolderPathsOfSearchHits(searchState: SearchState) {
  // setting to false, so that it won't trigger the watch in CompactSearchItem component before the new paths are loaded.
  searchState.ops.setPathsForNewFolderHitsHasBeenLoaded(false);
  await getFoldersAncestryFromServer(searchState, searchState.reactive.listOfFolderIdsInLastSearchResult.value);
}


export function renameFolderAndDependencies(searchState: SearchState, folder: Folder, newFolderName: string) {
  folder.name = newFolderName;
  searchState.ops.updateFolderInSearch({ id: folder.id, name: newFolderName });
  renameFolderInTree(folder.id, newFolderName);
  const currentWorkFolder = folders.workFolder;
  const isWorkFolder = currentWorkFolder !== null && folder.id === currentWorkFolder.id;
  if (isWorkFolder) updateWorkFolder(newFolderName);
  EventBus.emit(EVENTS.FOLDER_NAME_UPDATED, { folderId: folder.id, newName: newFolderName });
}


export function updateFolderOwnerAndDependencies(searchState: SearchState, folderId: string, newOwner: string) {
  updateFolderOwnerInTree(folderId, newOwner);
  searchState.ops.updateFolderInSearch({ id: folderId, owner: newOwner });
  EventBus.emit(EVENTS.FOLDER_OWNER_UPDATED, { folderId, newOwner });
}


function checkUnique(
  folderName: string,
  folderId: string | null,
  folderParent?: Folder,
  subFoldersForSearchHit?: FolderDto[],
) {

  let subFolders: FolderViewModel[] | FolderDto[] = [];

  if (folderParent && isFolderViewModel(folderParent) && folderParent.subTrees) {
    subFolders = folderParent.subTrees;
  } else if (subFoldersForSearchHit) {
    subFolders = subFoldersForSearchHit;
  }

  for (const f of subFolders) {
    if (!f.name || f.id === folderId) continue; // Defensive check for bad data
    if (f.name.toLowerCase() === folderName.toLowerCase()) {
      return false;
    }
  }

  return true;
}

function isFolderSearchHit(folder: Folder): folder is FolderSearchResultHit {
  return 'folderParents' in folder && folder.itemType === ItemType.FOLDER;
}


export async function doRename(searchState: SearchState, folder: Folder) {
  const oldName = folder.name;
  const currentFolder = folders.currentFolder;

  const dialogProps: BaseDialogProps = {
    maxWidth: 450,
    icon: 'create_new_folder',
    header: 'Rename folder',
    actionText: 'Rename',
    closeOnActionClick: true,
  };

  let parentFolderPath = '';
  let currentValue = '';

  const editProps: EditDialogProps = {
    startValue: folder.name ?? '',
    lazyValidation: true,
    placeholder: 'Folder name',
    rules: [(val: string) => validateFolderName(val) || 'Invalid characters in folder name',
            (val: string) => checkUnique(val, folder.id, cachedParentFolder, subFoldersForSearchHit) || 'Folder with that name already exists'],
    subHeaderHtml: new SafeHtmlBuilder()
      .addText('Editing folder name at: ').addLineBreakAndAppendText('').addBoldText(`${parentFolderPath}/${currentValue}`),
    valueListener: (newValue) => {
      currentValue = newValue;
      editProps.subHeaderHtml = new SafeHtmlBuilder()
        .addText('Editing folder name at: ').addLineBreakAndAppendText('').addBoldText(`${parentFolderPath}/${currentValue}`);
    }
  };

  let subFoldersForSearchHit: FolderDto[] | undefined;
  let cachedParentFolder: FolderViewModel | undefined;

  const currentWorkFolder = folders.workFolder;
  const isWorkFolder = currentWorkFolder !== null && folder.id === currentWorkFolder.id;

  void showEditDialog(dialogProps, editProps,
  async (folderName) => {
    const newFolderName = folderName.trim();
    if (oldName === newFolderName) return;

    try {
      renameFolderAndDependencies(searchState, folder, newFolderName);
      await renameFolder(folder.id, newFolderName);
      invalidatePathCacheForFolder(folder.id);
    } catch (err: any) {
      folder.name = oldName;
      searchState.ops.updateFolderInSearch({ id: folder.id, name: oldName });
      renameFolderInTree(folder.id, oldName);
      if (err?.response?.data?.error?.statusCode === 409) {
        logErrorAndShowToast(`There's already a folder with the name '${newFolderName}'`, err);
      } else {
        logErrorAndShowToast('Error faced while creating new folder', err);
      }
      if (isWorkFolder) updateWorkFolder(oldName);
    }
    if (currentFolder?.id === folder.id) {
      currentFolder.name = folder.name ?? '';
    }
  }, async () => {
    const promises = [];
    if (isFolderViewModel(folder)) {
      cachedParentFolder = folder.parent ? folders.tree[folder.parent] : undefined;
      if (cachedParentFolder && cachedParentFolder.hasSubfolder && !cachedParentFolder.hasLoadedSubFolders) {
        promises.push(fetchSubfolders(cachedParentFolder, undefined).catch(err => console.log(err)));
      }
    } else if (isFolderSearchHit(folder)) {
      if (folder.folderParents && folder.folderParents.length > 0) {
        promises.push(getFolderContent(folder.folderParents[0], { type: FolderTypeName }).then(result => subFoldersForSearchHit = result as FolderDto[])
        .catch(err => console.log(err)));
      }
    } else if (folder.parent) { // We have the parent not loaded in memory, but it is model from the folder tree
      promises.push(getFolderContent(folder.parent, { type: FolderTypeName }).then(result => subFoldersForSearchHit = result as FolderDto[])
      .catch(err => console.log(err)));
    }
    if (folder.parent) {
      promises.push(getUncachedPathStringifiedForFolder(folder.parent).then(path => {
        // We don't want to include the "root folder" name, nor do we need the current folder.
        if (path) {
          const folders_ = path.split('/').splice(2);
          if (folders_.length > 0) {
            parentFolderPath = `/${folders_.join('/')}`;
          }
        }
        editProps.subHeaderHtml = new SafeHtmlBuilder()
          .addText('Editing folder name at: ').addLineBreakAndAppendText('').addBoldText(`${parentFolderPath}/${currentValue}`);
      }));
    }
    return Promise.all(promises);
  });
}


export async function deleteFolderAndDependencies(searchState: SearchState, folder: Folder) {
  const currentWorkFolder = folders.workFolder;
  const isWorkFolder = currentWorkFolder !== null && folder.id === currentWorkFolder.id;
  if (isWorkFolder) {
    setWorkFolder(null);
    saveWorkFolderId(null);
  }
  const currentFolder = folders.currentFolder;
  const isInDeletedBranch = currentFolder !== null
    && tryToGetFoldersToRootFromCache(currentFolder.id, true).some(f => f.id === folder.id);
  removeFolderFromTree(folder.id);
  if (isInDeletedBranch) {
    setCurrentFolder(null);
    goToFolder({ id: FolderKind.TenantRoot, clearSearchString: false });
  }
  searchState.ops.removeFromSearchById(folder.id);

  // Only clear selections if we're currently viewing the deleted folder
  // OR if the item is the deleted folder itself
  const currentFolderId = folders.currentFolder?.id;
  const selectedItems = searchState.reactive.selectedSearchItems.value;

  const itemsToDeselect = selectedItems.filter(item => {
    // Always deselect if the item itself is the deleted folder
    if (item.id === folder.id) {
      return true;
    }

    // Only deselect non-folder items if we're currently viewing the deleted folder
    // This ensures items remain selected if they're visible through other folders
    if (currentFolderId === folder.id &&
        item.folderParents &&
        item.folderParents.includes(folder.id)) {
      return true;
    }

    return false;
  });

  // Remove only the related selections
  for (const item of itemsToDeselect) {
    searchState.ops.removeFromSelectedSearchItems(item);
  }

  invalidatePathCacheForFolder(folder.id);
  Vue.set(folder, 'folderState', FolderState.DELETING);
}


function buildFolderDeleteDialogProps(folder: Folder, folderPath: string, isWorkFolder: boolean) {
  const i18n = getI18n();

  const headerHtml = new SafeHtmlBuilder()
    .addText(i18n.t('DeleteFolderDialog.SimpleDialogHeader'))
    .addText(' ')
    .addBoldText(folder.name);

  const baseHtml = new SafeHtmlBuilder()
    .addText(i18n.t('DeleteFolderDialog.AskForConfirmation'))
    .addLineBreaks()
    .addBoldText(folderPath)
    .addLineBreaks(2)
    .addText(i18n.t('DeleteFolderDialog.AskForConfirmationInfoText'));

  if (isWorkFolder) {
    baseHtml.addLineBreaks()
      .addText(i18n.t('DeleteFolderDialog.AskForConfirmationInfoWorkFolder'));
  }

  return {
    icon: 'delete',
    header: '',
    headerHtml,
    baseHtml,
    maxWidth: 450,
    actionText: i18n.t('DeleteFolderDialog.SimpleDialogPrimaryActionText'),
    cancelText: i18n.t('DeleteFolderDialog.SimpleDialogCancelText'),
    closeOnEscape: true,
  };
}

export async function doDelete(store: StoreType, searchState: SearchState, folder: Folder) {
  const folderPath = await getUncachedPathStringifiedForFolder(folder.id) ?? folder.name;
  const isWorkFolder = folders.workFolder !== null && folder.id === folders.workFolder.id;
  const useAclAwareDelete = features.isFeatureEnabled('aclAwareFolderDelete');

  const req: FolderDeleteConfirmDialogRequest = {
    folder,
    dialogProps: buildFolderDeleteDialogProps(folder, folderPath, isWorkFolder),
    showDeleteExclusiveItemsCheckbox: useAclAwareDelete,
    onConfirm: async (deleteExclusiveItems: boolean) => {
      if (useAclAwareDelete) {
        showFolderDeleteDialog({
          folder,
          deleteExclusiveItems,
          onComplete: (hasFailures: boolean) => handleDeleteResult(store, searchState, folder, hasFailures),
        });
      } else {
        try {
          await deleteFolder(folder.id);
          deleteFolderAndDependencies(searchState, folder).catch(err => console.error(err));
        } catch (err) {
          EventBus.emit(EVENTS.REFRESH_CURRENT_SEARCH);
          logErrorAndShowToast('Error occurred when deleting the folder, please contact your system administrator.', err, 'ex in doDelete');
        }
      }
    },
  };
  showFolderDeleteConfirmDialog(req);
}

export async function handleDeleteResult(
  store: StoreType,
  searchState: SearchState,
  folder: Folder,
  hasFailures: boolean,
) {
  try {
    if (!hasFailures) {
      deleteFolderAndDependencies(searchState, folder).catch(err => console.error(err));
    } else {
      const currentWorkFolder = folders.workFolder;
      if (currentWorkFolder && currentWorkFolder.id === folder.id) {
        setWorkFolder(null);
        saveWorkFolderId(null);
      }
      await initFolderTree(
        globalBackendClient,
        store.getters.isFolderTreeActive,
        new AbortController().signal
      );
      EventBus.emit(EVENTS.REFRESH_CURRENT_SEARCH);
      searchState.ops.clearSelectedSearchItems();
    }
  } catch (err) {
    logErrorAndShowToast('Error occurred when refreshing the folder, please try again.', err, 'ex in handleDeleteResult');
    EventBus.emit(EVENTS.REFRESH_CURRENT_SEARCH);
  }
}

export async function doCreate(searchState: SearchState, folder: Folder | 'root', isFolderTreeActive: boolean) {
  const parentFolder: Folder = folder === 'root' ? (await getFolder(FolderKind.TenantRoot)) : folder;
  const dialogProps: BaseDialogProps = {
    maxWidth: 450,
    icon: 'create_new_folder',
    header: getI18n().t('FolderUtilsTS.NewFolder') as string,
    actionText: getI18n().t('FolderUtilsTS.Create') as string,
  };

  let subFoldersForSearchHit: FolderDto[] | undefined;
  let parentFolderPath = '';
  let currentValue = '';

  const editProps: EditDialogProps = {
    startValue:'',
    lazyValidation: true,
    label: getI18n().t('FolderUtilsTS.NewFolder') as string,
    rules: [(val: string) => validateFolderName(val) || 'Invalid characters in folder name',
            (val: string) => checkUnique(val, null, parentFolder ?? undefined, subFoldersForSearchHit) || 'Folder with that name already exists'],
    subHeaderRole: 'status',
    subHeaderAriaLabel: 'Folder Path',
    subHeaderHtml: new SafeHtmlBuilder()
      .addText('Create a new folder at: ').addLineBreakAndAppendText('').addBoldText(`${parentFolderPath}/${currentValue}`),
    valueListener: (newValue) => {
      currentValue = newValue;
      editProps.subHeaderHtml = new SafeHtmlBuilder()
        .addText('Create a new folder at: ').addLineBreakAndAppendText('').addBoldText(`${parentFolderPath}/${currentValue}`);
    }
  };

  void showEditDialog(dialogProps, editProps,
    async (folderName) => {
      invariant(parentFolder, 'Expected parentFolder');
      const newFolderName = folderName.trim();
      try {
        if (isFolderViewModel(parentFolder)) {
          parentFolder.loading = true;
        }

        if (!folders.currentFolder) searchState.ops.setPathsForNewFolderHitsHasBeenLoaded(false);
        const newFolder = await createFolder(newFolderName, parentFolder.id);

        if (isFolderViewModel(parentFolder)) {
          doExpandFolder(parentFolder.id, isFolderTreeActive);
          if (folders.tree[newFolder.id]) {
            EventBus.emit(EVENTS.NEW_FOLDER, folders.tree[newFolder.id]);
            return; // If cached, it means the folder was already created by a websocket event.
          }
          addFolderToParent(parentFolder.id, newFolder);
        }

        if (folders.currentFolder?.id === parentFolder.id) {
          // TODO: While migrating search-module from Vuex store to global object this revealed
          // that we are breaking types here.
          searchState.ops.injectFolderIntoSearchResult(newFolder as unknown as SearchResultHit);
        }

        EventBus.emit(EVENTS.NEW_FOLDER, folders.tree[newFolder.id]);
      } catch (err: any) {
        if (err?.response?.data?.error?.statusCode === 409) {
          logErrorAndShowToast(`Folder '${newFolderName}' cannot be created in folder '${parentFolder.name}'`, err);
        } else if (err?.response?.data?.error?.statusCode === 400) {
          logErrorAndShowToast('Invalid folder name: ' + newFolderName, err);
        } else {
          logErrorAndShowToast('Could not create folder: ' + newFolderName, err);
        }
      } finally {
        if (isFolderViewModel(parentFolder)) {
          parentFolder.loading = false;
        }
        if (!folders.currentFolder) searchState.ops.setPathsForNewFolderHitsHasBeenLoaded(true);

      }
    },
  async () => {
    invariant(parentFolder, 'Expected parentFolder');
    const promises = [];
    promises.push(getPathStringifiedForFolder(parentFolder.id).then(path => {
      // We don't want to include the "root folder" name.
      if (path) {
        const folders_ = path.split('/').splice(2);
        if (folders_.length > 0) {
          parentFolderPath = `/${folders_.join('/')}`;
        }
      }
      editProps.subHeaderHtml = new SafeHtmlBuilder()
        .addText('Create a new folder at: ').addLineBreakAndAppendText('').addBoldText(`${parentFolderPath}/${currentValue}`);
    }));

    // We need to load these beforehand so we can prevent the user from creating a non-unique folder
    if (isFolderViewModel(parentFolder)) {
      if (parentFolder.hasSubfolder && !parentFolder.hasLoadedSubFolders) {
        promises.push(fetchSubfolders(parentFolder, undefined).catch(err => console.log(err)));
      }
    } else {
      dialogProps.bootstrapping = true;
      promises.push(getFolderContent(parentFolder.id, { type: FolderTypeName }).then(result => subFoldersForSearchHit = result as FolderDto[])
      .catch(err => console.log(err)));
    }
    return Promise.all(promises);
  });
}

export async function doArchive(store: Store<any>, folder: FolderViewModel | FolderDto) {
  void showSimpleAsyncDialog(
    {
      header: 'Archive',
      headerHtml: new SafeHtmlBuilder().addText('Archive ').addBoldText(folder.name),
      icon: 'archive',
      baseHtml: new SafeHtmlBuilder().addText('This action will archive all the items of  ').addBoldText(folder.name),
      actionText: 'Archive',
      maxWidth: 500,
    },
    async () => {
      const req: ArchiveFolder = {
        operationType: OperationType.Archive,
        updateType: UpdateType.BULK,
        folderInfo: { id: folder.id, name: folder.name }
      };
      showArchiveBulkDialog(req);
    }
  );
}

export function doUnarchive(store: Store<any>, folder: FolderViewModel | FolderDto) {
  void showSimpleAsyncDialog(
    {
      header: 'Unarchive',
      headerHtml: new SafeHtmlBuilder().addText('Unarchive ').addBoldText(folder.name),
      icon: 'unarchive',
      baseHtml: new SafeHtmlBuilder().addText('This action will unarchive all the items of  ').addBoldText(folder.name),
      actionText: 'Unarchive',
      maxWidth: 500,
    },
    async () => {
      const req: ArchiveFolder = {
        operationType: OperationType.UnArchive,
        updateType: UpdateType.BULK,
        folderInfo: { id: folder.id, name: folder.name }
      };
      showArchiveBulkDialog(req);
    }
  );
}

export async function doBatchRemoveFromFolder(searchState: SearchState, items: SearchResultHit[]) {
  const folder = folders.currentFolder;
  if (folder === null) {
    // Should not happen, type safety.
    console.warn('No current folder found.');
    return;
  }
  try {
    // For more than 5 items we show a please wait dialog for the requests to process.
    if (items.length >= 5) {
      showPleaseWaitDialog(`Moving ${items.length} item${items.length === 1 ? '' : 's'} out of '${folder.name}' ..`);
    }

    // Optimistically remove everywhere in client.
    const promises = [];
    for (const item of items) {
      searchState.ops.removeFromSearchById(item.id);
      promises.push(removeItemFromFolder(folder.id, item.id));
    }
    searchState.ops.clearSelectedSearchItems();
    await Promise.all(promises);
    if (items.length >= 5) {
      hidePleaseWaitDialog();
    }
  } catch (err) {
    // Revert state from server call
    EventBus.emit(EVENTS.REFRESH_CURRENT_SEARCH);
    hidePleaseWaitDialog();
    logErrorAndShowToast('Error occurred when trying to remove items. Please contact your system administrator.', err, 'ex in doBatchFromRemoveFolder');
  }
}


/**
 * Historically we've always just assumed that whatever is currently selected, is what should be processed in drop events. However, we have now
 * added support for dragging single items despite possibly having a selection that does not contain the dragged item:
 *
 * 1) A user is dragging from the search view something else than what is currently selected (useful for creating relations)
 * 2) A user is dragging a related item from the quick preview pane, to for example put it in a folder or work folder.
 *
 * In both these scenarios we want to ignore the current selection, and use the draggedItem instead.
 */
export async function itemsDropped(store: Store<any>, searchState: SearchState, payload: ItemsDroppedEvent) {
  const {folderId, isHoldingCtrlOrAlt, dragObject } = payload;

  let itemsToProcess: DroppableItem[] = [];
  const selectedRawItems = searchState.reactive.selectedSearchItems.value;
  const selectedItemsContainsDraggedItem = selectedRawItems.find(item => item.id === dragObject.item.id) !== undefined;

  // We use the selected items in search when both the count is higher than 1 and the selection contains the id of the item where the drag
  // operation started. If either of these are false, we can assume the user is dragging a single item from somewhere else, and
  // consequently will process the item in the drag object.
  if (selectedRawItems.length > 1 && selectedItemsContainsDraggedItem) {
    itemsToProcess = selectedRawItems;
  } else {
    itemsToProcess = [dragObject.item];
  }

  const hasItselfInSelection = itemsToProcess.find(hit => hit.id === folderId);
  if (hasItselfInSelection) {
    showToast('Can not move a folder onto itself', TOAST.INFO);
  }

  const selectedItems = itemsToProcess.filter(hit => hit.id !== folderId);

  if (selectedItems.length === 0) {
    return;
  }

  const currentUser = store.getters.user as User;
  const permissions = currentUser.permissions as string[];
  const hasItemMovePermission = canMoveItems(permissions);

  if (!isAnyFolderInSelectedItems(selectedItems)) {
    /*
      We want to prevent the user from moving items to folders if they do not have the permission to do so.
      This is because the user might have the permission to create items or move folders, but do not have
      the permission to move items to folders. In this case, we want to show a toast message to the user.
    */
    if (!hasItemMovePermission) {
      showToast('User does not have permission to add or move items to folders.', TOAST.WARNING);
      return;
    }
    await doMoveOrCopyOperation(payload, selectedItems);
  } else {
    // We always move folders. In case of items, it depends on whether the default operation type
    // for items is move or copy in tenant config and if the user is holding ctrl or alt
    let operationName: string = 'Move';
    if (
      getNonFoldersCount(selectedItems)
      && getOpTypeOnCtrlOrAltHold(isHoldingCtrlOrAlt, store.getters.tenantConfig.isMoveDefault) === 'Copy'
    ) {
      operationName = 'Move/Copy';
    }

    const { movableItems: moveOrCopyableItems } = getMovableAndNonMovableItems(selectedItems, currentUser);

    let pathOfDestinationFolder = await getPathStringifiedForFolder(folderId);
    invariant(pathOfDestinationFolder !== null, 'Expected pathOfDestinationFolder not to be null');
    const slashes = pathOfDestinationFolder.split('/').splice(2);
    pathOfDestinationFolder = `/${slashes.join('/')}`;

    const baseHtml = getBaseHtmlForItemsDropped({
      operationName,
      droppedItems: selectedItems,
      pathOfDestinationFolder,
      user: currentUser,
    });

    showMoveOrCopyDialog({
      header : `${operationName} Operation`,
      baseHtml,
      icon: 'stop',
      actionText: moveOrCopyableItems.length ? operationName : 'Okay',
    }, doMoveOrCopyOperation, payload, moveOrCopyableItems);
  }
}

export async function loadFolderPathForItem(
  isProxyMode: boolean,
  applicationContext: ElectronContext | AdobePanelContext,
  dispatch: Dispatch,
  payload: SearchResultHit,
) {
  if (payload.itemType === ItemType.FOLDER || payload.itemType === ItemType.PERSON) {
    return;
  }

  try {
    const paths = await getFolderAncestry([payload.id], 'item');
    const resolvedPaths = paths[payload.id];
    if (!resolvedPaths) {
      void dispatch('setLocalPathInfo', { id: payload.id, localPathInfo: prepareLocalPathInfo(isProxyMode, applicationContext, payload, null) }, { root: true });
      return;
    }

    const flattenItemAncestors = Object.entries(resolvedPaths.paths).reduce(
      (acc: FolderPathSegmentDto[][], [_, ancestor]) => acc.concat(ancestor),
      [],
    );
    invariant(flattenItemAncestors.filter(el => el == null).length == 0, 'loadFolderPathForItem: Did not expect any null elements');
    void dispatch('setLocalPathInfo', { id: payload.id, localPathInfo: prepareLocalPathInfo(isProxyMode, applicationContext, payload, flattenItemAncestors) }, { root: true });
  } catch (err) {
    logErrorAndShowToast('Something went wrong when loading folder path', err, 'ex in loadFolderPathForItem');
  }
}


export async function loadFolderPaths(
  isProxyMode: boolean,
  applicationContext: ElectronContext | AdobePanelContext,
  dispatch: Dispatch,
  payload: SearchResultHit[],
) {
  const mediaOnly = payload.filter(item => item.itemType !== ItemType.FOLDER && item.itemType !== ItemType.PERSON);
  const ids = mediaOnly.map(item => item.id);

  if (ids.length === 0) {
    return;
  }

  let paths: AncestryResponseDto | null = null;

  try {
    paths = await getFolderAncestry(ids, 'item');
  } catch (err) {
    logErrorAndShowToast('Unable to load folder path', err, 'ex in loadFolderPaths');
  }

  if (paths === null) {
    return;
  }

  let allUpdates = [];
  for (let i = 0; i < mediaOnly.length; i++) {
    const hit = mediaOnly[i];

    // figuring out the paths here is apparently a cpu-intensive work. we call defer here
    // in order to let UI rendering proceed between every iteration, since otherwise it appears to stall it.
    await defer();

    const resolvedPaths = paths[hit.id];
    if (!resolvedPaths) {
      /*
        TODO: This code is very suspect. Using Vue.set() is an indication
        that this code may be trying to handle the case in which the state
        given in the loadFolderPaths payload parameter is already stored in
        the Vuex store. If that is the case, it is a Vuex API contract
        violation to modify this state outside of a mutation function.

        In fact, it appears that the searchResultsReceived action from
        search-module were calling loadFolderPath, initiating an
        asynchronous action, and then without waiting for the action to
        complete committed a new itemSearch state giving in an object
        structure that contains substructures that loadFolderPaths was not
        finished updating.

        There are performance reasons for why the loadFolderPaths() action
        is not awaited.

        The Vue.set() usage here may be a workaround for the underlying
        problem that searchResultsReceived was not awaiting, and could
        potentially be removed if we make sure that the Promise returned by
        loadFolderPaths is awaited before committing the state it produces.

        We should also consider whether loadFolderPaths is intended to be a
        function that causes mutations in the store, or this is merely a
        function for preparing new state that will be committed to the store
        elsewhere. If the latter, we should move this function out as a
        traditional stand-alone function, rather than providing it as a
        store action.
      */
      allUpdates.push({ id: hit.id, localPathInfo: prepareLocalPathInfo(isProxyMode, applicationContext, hit, null) });
      continue;
    }

    const flattenItemAncestors = flatten(Object.values(resolvedPaths.paths));
    invariant(flattenItemAncestors.filter(el => el == null).length == 0, 'loadFolderPaths: Did not expect any null elements');
    allUpdates.push({ id: hit.id, localPathInfo: prepareLocalPathInfo(isProxyMode, applicationContext, hit, flattenItemAncestors) });

    if (i % 15 === 0) {
      void dispatch('setLocalPathInfos', allUpdates, { root: true });
      allUpdates = [];
    }
  }

  void dispatch('setLocalPathInfos', allUpdates, { root: true });
}


export async function showImportFolderDialog(folder: Folder) {
  try {
    let currentPath = currentFolderPath.value;
    const parent = folder.parent;
    if (currentPath.length < 1 || currentPath[currentPath.length - 1].folderId !== parent) {
      currentPath = [];
      if (parent) {
        let currentFolder = await getFolderFromCacheOrServer(parent);
        while (currentFolder.kind !== FolderKind.TenantRoot) {
          currentPath.unshift({ folderId: currentFolder.id, name: currentFolder.name });
          if (!currentFolder.parent) break;
          currentFolder = await getFolderFromCacheOrServer(currentFolder.parent);
        }
      }
    }

    const folderPath = [...currentPath, {
      folderId: folder.id,
      name: folder.name
    }];

    return showImportFolderDialog_(folder, folderPath);
  } catch (error) {
    logErrorAndShowToast('Failed to import folder, please try again.', error, 'ex in showImportFolderDialog');
  }
}


/**
 * Note that this function is used for machine readable paths as well as display
 * labels. Avoid doing changes here to alter the display labels, since that will
 * break the machine readable paths.
 */
export async function getTargetPathFromFolderId(folderId: string, includeTenantSpace = false, separatorForJoin = '/', relativePath = false) {
  const path = await getPathStringifiedForFolder(folderId);
  if (path) {
    if (includeTenantSpace) return path;
    let slashes = path.split('/').splice(2);
    if (relativePath) {
      slashes = slashes.slice(0, -1);
      if (!slashes.length) return undefined;
    }
    return slashes.join(separatorForJoin);
  }
}

export function shouldLoadPathForEachFolderHit(store: Store<any>) {
  if (store.getters.includeSubfolders) return true;
  return folders.currentFolder === null;
}

export function constructLoadedSearchItem(folderDto: FolderDto): LoadedSearchItem {
  return {
    id: folderDto.id,
    owner: folderDto.owner,
    name: folderDto.name,
    createdOn: folderDto.createdOn,
    parent: folderDto.parent,
    itemType: folderDto.itemType,
    visibleTo: folderDto.visibleTo,
    updatableBy: folderDto.updatableBy,
    itemState: ItemState.COMPLETE,
    folderParents: [],
    proxy: null,
    metadata: folderDto.metadata ?? { formId: FOLDER_DEFAULT_MDF_ID, formData: {} },
    metadataVersion: 1,
    metadataSnapshot: {
      latestSnapshot: folderDto.metadata ?? { formId: FOLDER_DEFAULT_MDF_ID, formData: {} },
      metadataVersion: 1,
      conflictedFields: [],
    },
    hasRelations: false,
  };
}

export async function handleFolderReOrdering(
  payload: {
    targetFolder: FolderViewModel,
    draggedFolder: ClientItem,
    placeAtTop: boolean,
  },
  store: Store<any>,
  searchState: SearchState,
) {
  const { targetFolder, draggedFolder, placeAtTop } = payload;
  if (!targetFolder.parent) return;
  if (targetFolder.id === draggedFolder.id) {
    showToast('Folder is already in the same position', TOAST.INFO);
    return;
  }

  const parentFolder = folders.tree[targetFolder.parent];
  const user = store.getters.user as User;
  if (!isFolderUpdatableByUser(parentFolder.updatableBy, parentFolder.owner, parentFolder.kind, user)) {
    showToast(`The folder '${parentFolder.name}' is not updatable`, TOAST.WARNING);
    return;
  }

  const siblingFolders = parentFolder?.subTrees?.filter(folder => !folder.isShortcut) ?? [];
  const targetIndex = siblingFolders.findIndex(folder => folder.id === targetFolder.id);
  if (targetIndex === -1) return;

  try {
    const newSortKey = calculateNewSortKey(targetIndex, siblingFolders, targetFolder, placeAtTop);
    const newParentId = targetFolder.parent !== draggedFolder.parent ? targetFolder.parent : undefined;

    if (newParentId) {
      await moveAndReorderFolder(store, searchState, draggedFolder, newSortKey, newParentId);
      return;
    }

    await updateFolderSortKeyAndParent(draggedFolder.id, newSortKey, newParentId);
    updateSortKeyInTree(draggedFolder.id, newSortKey);
  } catch (error) {
    logErrorAndShowToast('Failed to reorder folder, please try again.', error, 'Error in handleFolderReOrdering');
  }
}

function calculateNewSortKey(
  targetIndex: number,
  siblingFolders: FolderViewModel[],
  targetFolder: FolderViewModel,
  placeAtTop: boolean
): string {
  if (targetIndex === 0 && placeAtTop) {
    return calculateSortKey(null, targetFolder);
  } else if (targetIndex === siblingFolders.length - 1 && !placeAtTop) {
    return calculateSortKey(targetFolder, null);
  } else if (placeAtTop) {
    return calculateSortKey(siblingFolders[targetIndex - 1], targetFolder);
  } else {
    return calculateSortKey(targetFolder, siblingFolders[targetIndex + 1]);
  }
}

function handleMoveFolder(searchState: SearchState, draggedFolder: ClientItem, newParentId: string) {
  const previousParentId = draggedFolder.parent!;
  moveFolder(previousParentId, newParentId, draggedFolder.id).catch(err => console.error(err));
  invalidatePathCacheForFolder(draggedFolder.id);

  const currentFolderId = folders.currentFolder?.id;
  if (currentFolderId === previousParentId) {
    searchState.ops.removeIdsFromSearchResult({ onlyFolders: true, idsToRemove: [draggedFolder.id] });
  }

  const searchResults = searchState.reactive.itemSearch.value.results.hits;
  if (currentFolderId === newParentId && !searchResults.some(hit => hit.id === draggedFolder.id)) {
    searchState.ops.injectFolderIntoSearchResult(draggedFolder);
  }
}

async function moveAndReorderFolder(
  store: Store<any>,
  searchState: SearchState,
  draggedFolder: ClientItem,
  newSortKey: string | null,
  newParentId: string
) {
  let pathOfDestinationFolder = await getPathStringifiedForFolder(newParentId);
  invariant(pathOfDestinationFolder !== null, 'Expected pathOfDestinationFolder not to be null');
  const slashes = pathOfDestinationFolder.split('/').splice(2);
  pathOfDestinationFolder = `/${slashes.join('/')}`;

  const baseHtml = getBaseHtmlForItemsDropped({
    operationName: 'Move and reorder',
    droppedItems: [draggedFolder],
    pathOfDestinationFolder,
    user: store.getters.user as User,
  });

  await showSimpleAsyncDialog(
    {
      header: 'Folder Move and Reorder',
      baseHtml,
      icon: 'mdi-folder-arrow-up-down',
      actionText: 'Move and Reorder',
      maxWidth: 450,
    },
    async () => {
      try {
        await updateFolderSortKeyAndParent(draggedFolder.id, newSortKey, newParentId);
        handleMoveFolder(searchState, draggedFolder, newParentId);
        updateSortKeyInTree(draggedFolder.id, newSortKey);
      } catch (error) {
        // Surface a clear toast and allow the dialog to close gracefully
        logErrorAndShowToast('Failed to move and reorder folder.', error, 'Error in moveAndReorderFolder');
      }
    }
  );
}
